import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Archive as ArchiveIcon, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "customers", label: "Kunden" },
  { key: "suppliers", label: "Lieferanten" },
  { key: "terminals", label: "Kassen" },
  { key: "locations", label: "Lagerorte" },
];

function formatDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("de-DE");
}

export default function Archiv() {
  const [tab, setTab] = useState("customers");
  const qc = useQueryClient();

  const data = useQuery({
    queryKey: ["archive"],
    queryFn: async () => (await api.get("/archive")).data,
  });

  const restore = useMutation({
    mutationFn: async ({ kind, id }) => {
      const map = { customer: "customers", supplier: "suppliers", location: "locations", terminal: "terminals" };
      return (await api.post(`/archive/${map[kind]}/${id}/restore`)).data;
    },
    onSuccess: () => {
      toast.success("Wiederhergestellt");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const confirmRestore = (kind, id, label) => {
    if (window.confirm(`"${label}" wiederherstellen? Prüft Duplikate und historische Konflikte.`)) {
      restore.mutate({ kind, id });
    }
  };

  const rows = data.data?.[tab] || [];

  const renderRow = (r) => {
    let title = r.name || "—";
    let sub = [];
    if (r.kind === "customer") {
      sub.push(r.email || "—");
      if (r.club_card_id) sub.push(`Karte: ${r.club_card_id}`);
      if (r.custom_number) sub.push(`Kd-Nr: ${r.custom_number}`);
    } else if (r.kind === "supplier") {
      if (r.contact_person) sub.push(r.contact_person);
      if (r.contact_email) sub.push(r.contact_email);
      if (r.phone) sub.push(r.phone);
    } else if (r.kind === "location") {
      sub.push(`Code: ${r.code}`, `Typ: ${r.type}`);
      if (r.address) sub.push(r.address);
    } else if (r.kind === "terminal") {
      sub.push(`Lagerort-ID: ${r.location_id}`);
    }
    return (
      <div key={r.id} data-testid={`archive-row-${r.kind}-${r.id}`}
        className="border border-zinc-800 bg-zinc-900 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{title}</div>
          <div className="text-xs text-zinc-500 break-anywhere">{sub.filter(Boolean).join(" · ") || "—"}</div>
          <div className="text-xs text-zinc-600 font-mono mt-1">
            Archiviert: {formatDate(r.deleted_at)} · ID: {r.id.slice(0, 8)}…
          </div>
        </div>
        <Button
          data-testid={`archive-restore-${r.kind}-${r.id}`}
          size="sm"
          variant="outline"
          className="border-zinc-700 text-cyan-400 shrink-0"
          onClick={() => confirmRestore(r.kind, r.id, title)}
        >
          <RotateCcw size={14} className="mr-1" /> Wiederherstellen
        </Button>
      </div>
    );
  };

  return (
    <div data-testid="archive-page">
      <div className="mb-6 flex items-center gap-3 min-w-0">
        <ArchiveIcon className="text-cyan-400 shrink-0" size={28} />
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">Verwaltung</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">Archiv</h1>
        </div>
      </div>

      <div className="text-xs text-zinc-500 mb-4 border border-zinc-800 rounded-sm p-3 bg-zinc-900">
        Read-only. Archivierte Datensätze bleiben mit historischen Referenzen verknüpft und erscheinen nicht in operativen Auswahllisten.
        Wiederherstellen ist nur ohne Duplikat-Konflikt möglich.
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="archive-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`archive-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 rounded-sm text-xs uppercase tracking-wider border ${
              tab === t.key
                ? "border-cyan-400 bg-zinc-900 text-cyan-400"
                : "border-zinc-800 text-zinc-400 hover:text-zinc-100"
            }`}
          >
            {t.label} · {(data.data?.[t.key] || []).length}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid={`archive-list-${tab}`}>
        {rows.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500 text-sm">
            Keine archivierten Einträge in dieser Kategorie.
          </div>
        ) : rows.map(renderRow)}
      </div>
    </div>
  );
}
